import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from "@chakra-ui/react";
import FinancePageShell from "./common/FinancePageShell";
import FinanceSectionCard from "./common/FinanceSectionCard";
import FinanceStatCard from "./common/FinanceStatCard";
import { PayoutDestinationPayload, financeApi } from "./financeApi";
import { formatDateTime } from "./common/formatters";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { useAdminAccess } from "../Analytics/shared/AdminAccess/AdminAccessContext";

type PayoutDestinationRow = {
  _id?: string;
  ownerType?: "seller" | "driver" | string;
  ownerId?: string;
  ownerUserId?: string;
  sellerUserId?: string;
  accountUserId?: string;
  userId?: string;
  method?: "bank" | "airtel_money" | "tnm_mpamba" | string;
  displayName?: string;
  accountName?: string;
  bank?: {
    bankName?: string;
    accountNumber?: string;
    branchCode?: string;
    swiftCode?: string;
  };
  mobileMoney?: {
    provider?: string;
    phoneNumber?: string;
  };
  verificationStatus?: "pending" | "verified" | "failed" | string;
  isDefault?: boolean;
  createdAt?: string;
  metadata?: {
    createdBy?: string;
    ownerId?: string;
    ownerUserId?: string;
    sellerUserId?: string;
    accountUserId?: string;
    userId?: string;
    [key: string]: any;
  };
};

type DestinationSummary = {
  count: number;
  verified: number;
  pending: number;
  failed: number;
};

const clean = (value: unknown) => String(value || "").trim();

const normalizeRole = (value: unknown) =>
  clean(value)
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const toRows = (payload: any): PayoutDestinationRow[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;

  return [];
};

const getDestinationOwnerIds = (row: PayoutDestinationRow) => {
  const ids = new Set<string>();

  const add = (value: unknown) => {
    const id = clean(value);
    if (id) ids.add(id);
  };

  add(row.ownerId);
  add(row.ownerUserId);
  add(row.sellerUserId);
  add(row.accountUserId);

  const metadata = row.metadata || {};

  add(metadata.ownerId);
  add(metadata.ownerUserId);
  add(metadata.sellerUserId);
  add(metadata.accountUserId);

  /*
   * Legacy fallback only. Payout destinations should normally use ownerId.
   * The generic userId or metadata creator is considered only when no proper
   * ownership field exists.
   */
  if (ids.size === 0) {
    add(row.userId);
    add(metadata.userId);
    add(metadata.createdBy);
  }

  return ids;
};

const isDestinationOwnedByAccount = (
  row: PayoutDestinationRow,
  userId: string,
) => getDestinationOwnerIds(row).has(clean(userId));

const buildSummary = (rows: PayoutDestinationRow[]): DestinationSummary =>
  rows.reduce<DestinationSummary>(
    (summary, row) => {
      const status = clean(row.verificationStatus).toLowerCase() || "pending";

      summary.count += 1;

      if (status === "verified") {
        summary.verified += 1;
      } else if (status === "failed") {
        summary.failed += 1;
      } else {
        summary.pending += 1;
      }

      return summary;
    },
    {
      count: 0,
      verified: 0,
      pending: 0,
      failed: 0,
    },
  );

const getVerificationScheme = (status?: string) => {
  const normalized = clean(status).toLowerCase();

  if (normalized === "verified") return "green";
  if (normalized === "failed") return "red";

  return "orange";
};

const AdminPayoutDestinationsPage: React.FC = () => {
  const toast = useToast();
  const { theme } = useCustomTheme();
  const {
    userId,
    role,
    isSuperAdmin,
    loading: accessLoading,
  } = useAdminAccess();

  const normalizedRole = normalizeRole(role);
  const hasFinanceAdminAccess = isSuperAdmin || normalizedRole === "admin";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyRowId, setBusyRowId] = useState<string | null>(null);

  const [rows, setRows] = useState<PayoutDestinationRow[]>([]);
  const [summary, setSummary] = useState<DestinationSummary>({
    count: 0,
    verified: 0,
    pending: 0,
    failed: 0,
  });

  const [ownerType, setOwnerType] = useState<"seller" | "driver">("seller");
  const [ownerId, setOwnerId] = useState("");
  const [method, setMethod] = useState<"bank" | "airtel_money" | "tnm_mpamba">(
    "airtel_money",
  );

  const [displayName, setDisplayName] = useState("");
  const [accountName, setAccountName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [branchCode, setBranchCode] = useState("");
  const [isDefault, setIsDefault] = useState(true);

  useEffect(() => {
    if (accessLoading || !userId) return;

    if (!isSuperAdmin) {
      setOwnerType("seller");
      setOwnerId(userId);
    }
  }, [accessLoading, isSuperAdmin, userId]);

  const canManageDestination = useCallback(
    (row: PayoutDestinationRow) =>
      Boolean(
        userId && (isSuperAdmin || isDestinationOwnedByAccount(row, userId)),
      ),
    [isSuperAdmin, userId],
  );

  const load = useCallback(async () => {
    if (accessLoading) return;

    if (!userId || !hasFinanceAdminAccess) {
      setRows([]);
      setSummary(buildSummary([]));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const params: Record<string, string | number | boolean | undefined> = {};

      /*
       * Query parameters are request hints only. The response is always
       * filtered again below because this task intentionally makes no backend
       * authorization changes.
       */
      if (!isSuperAdmin) {
        params.ownerId = userId;
        params.ownerUserId = userId;
        params.accountUserId = userId;
      }

      const response = await financeApi.getPayoutDestinations(params);
      const returnedRows = toRows(response);

      const visibleRows = isSuperAdmin
        ? returnedRows
        : returnedRows.filter((row) =>
            isDestinationOwnedByAccount(row, userId),
          );

      setRows(visibleRows);
      setSummary(buildSummary(visibleRows));
    } catch (error: any) {
      console.error("Failed to load payout destinations:", error);

      toast({
        status: "error",
        title: "Failed to load payout destinations",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Please try again.",
        duration: 4000,
        isClosable: true,
      });

      setRows([]);
      setSummary(buildSummary([]));
    } finally {
      setLoading(false);
    }
  }, [accessLoading, hasFinanceAdminAccess, isSuperAdmin, toast, userId]);

  useEffect(() => {
    if (accessLoading) return;
    void load();
  }, [accessLoading, load]);

  const resetForm = useCallback(() => {
    setOwnerType("seller");
    setOwnerId(isSuperAdmin ? "" : userId);
    setMethod("airtel_money");
    setDisplayName("");
    setAccountName("");
    setPhoneNumber("");
    setBankName("");
    setAccountNumber("");
    setBranchCode("");
    setIsDefault(true);
  }, [isSuperAdmin, userId]);

  const createDestination = async () => {
    if (!userId || !hasFinanceAdminAccess) {
      toast({
        status: "error",
        title: "Access denied",
        description:
          "Only admin and superadmin accounts can add payout destinations.",
      });
      return;
    }

    const effectiveOwnerType: "seller" | "driver" = isSuperAdmin
      ? ownerType
      : "seller";

    const effectiveOwnerId = isSuperAdmin ? clean(ownerId) : userId;

    if (!effectiveOwnerId) {
      toast({
        status: "warning",
        title: "Owner ID is required",
      });
      return;
    }

    if (!clean(accountName)) {
      toast({
        status: "warning",
        title: "Account name is required",
      });
      return;
    }

    if (method === "bank" && !clean(accountNumber)) {
      toast({
        status: "warning",
        title: "Bank account number is required",
      });
      return;
    }

    if (method !== "bank" && !clean(phoneNumber)) {
      toast({
        status: "warning",
        title: "Mobile money phone number is required",
      });
      return;
    }

    try {
      const payload: PayoutDestinationPayload = {
        ownerType: effectiveOwnerType,
        ownerId: effectiveOwnerId,
        method,
        displayName: clean(displayName),
        accountName: clean(accountName),
        isDefault,
        createdBy: userId,
        bank:
          method === "bank"
            ? {
                bankName: clean(bankName),
                accountNumber: clean(accountNumber),
                branchCode: clean(branchCode),
              }
            : undefined,
        mobileMoney:
          method !== "bank"
            ? {
                phoneNumber: clean(phoneNumber),
              }
            : undefined,
      };

      setSaving(true);
      await financeApi.createPayoutDestination(payload);

      toast({
        status: "success",
        title: "Payout destination added",
        description:
          "Verify it before it is used for automatic payout batches.",
        duration: 3000,
        isClosable: true,
      });

      resetForm();
      await load();
    } catch (error: any) {
      console.error("Failed to add payout destination:", error);

      toast({
        status: "error",
        title: "Failed to add payout destination",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Please try again.",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const verify = async (
    row: PayoutDestinationRow,
    status: "verified" | "failed",
  ) => {
    if (!row._id || !canManageDestination(row)) {
      toast({
        status: "error",
        title: "Access denied",
        description:
          "You can update only payout destinations linked to your account.",
      });
      return;
    }

    try {
      setBusyRowId(row._id);

      await financeApi.verifyPayoutDestination(row._id, {
        verificationStatus: status,
        verificationNote:
          status === "verified"
            ? `Verified by ${userId}`
            : `Verification failed by ${userId}`,
        verifiedBy: userId,
      });

      toast({
        status: status === "verified" ? "success" : "warning",
        title:
          status === "verified"
            ? "Destination verified"
            : "Destination marked failed",
      });

      await load();
    } catch (error: any) {
      console.error("Failed to update destination verification:", error);

      toast({
        status: "error",
        title: "Failed to update verification",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Please try again.",
      });
    } finally {
      setBusyRowId(null);
    }
  };

  const setDefault = async (row: PayoutDestinationRow) => {
    if (!row._id || !canManageDestination(row)) {
      toast({
        status: "error",
        title: "Access denied",
        description:
          "You can update only payout destinations linked to your account.",
      });
      return;
    }

    try {
      setBusyRowId(row._id);
      await financeApi.setDefaultPayoutDestination(row._id);

      toast({
        status: "success",
        title: "Default destination updated",
      });

      await load();
    } catch (error: any) {
      console.error("Failed to set default destination:", error);

      toast({
        status: "error",
        title: "Failed to set default destination",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Please try again.",
      });
    } finally {
      setBusyRowId(null);
    }
  };

  const visibleOwnerId = useMemo(
    () => (isSuperAdmin ? ownerId : userId),
    [isSuperAdmin, ownerId, userId],
  );

  if (accessLoading) {
    return (
      <FinancePageShell title="Payout Destinations">
        <VStack py={10}>
          <Spinner />
          <Text color={theme.textMuted}>Checking account access...</Text>
        </VStack>
      </FinancePageShell>
    );
  }

  if (!userId) {
    return (
      <FinancePageShell title="Payout Destinations">
        <Center minH="260px">
          <Text color={theme.textMuted}>
            Sign in before opening payout destinations.
          </Text>
        </Center>
      </FinancePageShell>
    );
  }

  if (!hasFinanceAdminAccess) {
    return (
      <FinancePageShell title="Payout Destinations">
        <Center minH="260px">
          <Text color={theme.textMuted}>
            Only admin and superadmin accounts can manage payout destinations.
          </Text>
        </Center>
      </FinancePageShell>
    );
  }

  return (
    <FinancePageShell
      title="Payout Destinations"
      subtitle={
        isSuperAdmin
          ? "Manage seller and driver payout accounts across every platform account."
          : "Manage the seller payout accounts linked only to your account."
      }
    >
      <HStack mb={4} spacing={2} flexWrap="wrap">
        <Badge
          borderRadius="full"
          px={3}
          py={1}
          colorScheme={isSuperAdmin ? "purple" : "blue"}
        >
          {isSuperAdmin ? "All platform accounts" : "Current account only"}
        </Badge>

        <Text fontSize="xs" color={theme.textMuted}>
          Role: {role || "user"} · UID: {userId}
        </Text>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FinanceStatCard label="Destinations" value={String(summary.count)} />
        <FinanceStatCard label="Verified" value={String(summary.verified)} />
        <FinanceStatCard label="Pending" value={String(summary.pending)} />
      </SimpleGrid>

      <FinanceSectionCard
        title={
          isSuperAdmin
            ? "Add payout destination"
            : "Add your payout destination"
        }
      >
        <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
          <FormControl>
            <FormLabel>Owner Type</FormLabel>
            <Select
              value={isSuperAdmin ? ownerType : "seller"}
              onChange={(event) =>
                setOwnerType(event.target.value as "seller" | "driver")
              }
              isDisabled={!isSuperAdmin}
              bg={theme.inputBg}
              borderColor={theme.inputBorder}
            >
              <option value="seller">Seller</option>
              <option value="driver">Driver</option>
            </Select>

            {!isSuperAdmin && (
              <Text mt={1} fontSize="xs" color={theme.textMuted}>
                Admin accounts can add only their own seller payout destination.
              </Text>
            )}
          </FormControl>

          <FormControl>
            <FormLabel>Owner ID</FormLabel>
            <Input
              value={visibleOwnerId}
              onChange={(event) => setOwnerId(event.target.value)}
              isReadOnly={!isSuperAdmin}
              bg={theme.inputBg}
              borderColor={theme.inputBorder}
              cursor={!isSuperAdmin ? "not-allowed" : "text"}
            />

            {!isSuperAdmin && (
              <Text mt={1} fontSize="xs" color={theme.textMuted}>
                Your signed-in Firebase UID is used automatically.
              </Text>
            )}
          </FormControl>

          <FormControl>
            <FormLabel>Method</FormLabel>
            <Select
              value={method}
              onChange={(event) =>
                setMethod(
                  event.target.value as "bank" | "airtel_money" | "tnm_mpamba",
                )
              }
              bg={theme.inputBg}
              borderColor={theme.inputBorder}
            >
              <option value="airtel_money">Airtel Money</option>
              <option value="tnm_mpamba">TNM Mpamba</option>
              <option value="bank">Bank</option>
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Account Name</FormLabel>
            <Input
              value={accountName}
              onChange={(event) => setAccountName(event.target.value)}
              bg={theme.inputBg}
              borderColor={theme.inputBorder}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Display Name</FormLabel>
            <Input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              bg={theme.inputBg}
              borderColor={theme.inputBorder}
            />
          </FormControl>

          {method === "bank" ? (
            <>
              <FormControl>
                <FormLabel>Bank Name</FormLabel>
                <Input
                  value={bankName}
                  onChange={(event) => setBankName(event.target.value)}
                  bg={theme.inputBg}
                  borderColor={theme.inputBorder}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Account Number</FormLabel>
                <Input
                  value={accountNumber}
                  onChange={(event) => setAccountNumber(event.target.value)}
                  bg={theme.inputBg}
                  borderColor={theme.inputBorder}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Branch Code</FormLabel>
                <Input
                  value={branchCode}
                  onChange={(event) => setBranchCode(event.target.value)}
                  bg={theme.inputBg}
                  borderColor={theme.inputBorder}
                />
              </FormControl>
            </>
          ) : (
            <FormControl>
              <FormLabel>Mobile Money Phone</FormLabel>
              <Input
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(event.target.value)}
                bg={theme.inputBg}
                borderColor={theme.inputBorder}
              />
            </FormControl>
          )}
        </SimpleGrid>

        <HStack mt={4} justify="space-between" flexWrap="wrap">
          <Checkbox
            isChecked={isDefault}
            onChange={(event) => setIsDefault(event.target.checked)}
          >
            Make default destination
          </Checkbox>

          <Button
            onClick={createDestination}
            isLoading={saving}
            color={theme.textOnPrimary}
            bgImage={theme.gradients.primaryButton}
            _hover={{
              bgImage: theme.gradients.primaryButtonHover,
            }}
          >
            Add Destination
          </Button>
        </HStack>
      </FinanceSectionCard>

      <FinanceSectionCard
        title={isSuperAdmin ? "All Destinations" : "Your Destinations"}
      >
        {loading ? (
          <VStack py={10}>
            <Spinner />
            <Text color={theme.textMuted}>Loading payout destinations...</Text>
          </VStack>
        ) : (
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Owner</Th>
                  <Th>Method</Th>
                  <Th>Destination</Th>
                  <Th>Status</Th>
                  <Th>Default</Th>
                  <Th>Created</Th>
                  <Th />
                </Tr>
              </Thead>

              <Tbody>
                {rows.map((row) => {
                  const rowId = clean(row._id);
                  const rowBusy = Boolean(rowId) && busyRowId === rowId;
                  const canManage = canManageDestination(row);

                  return (
                    <Tr key={rowId || `${row.ownerId}-${row.method}`}>
                      <Td>
                        <Text fontWeight="700">
                          {isSuperAdmin
                            ? row.ownerType || "seller"
                            : "Current account"}
                        </Text>

                        <Text fontSize="xs" color={theme.textMuted}>
                          {isSuperAdmin ? row.ownerId || "—" : userId}
                        </Text>
                      </Td>

                      <Td>{row.method || "—"}</Td>

                      <Td>
                        <Text fontWeight="700">{row.accountName || "—"}</Text>

                        <Text fontSize="xs" color={theme.textMuted}>
                          {row.method === "bank"
                            ? `${row.bank?.bankName || ""} ${
                                row.bank?.accountNumber || ""
                              }`.trim() || "—"
                            : row.mobileMoney?.phoneNumber || "—"}
                        </Text>
                      </Td>

                      <Td>
                        <Badge
                          colorScheme={getVerificationScheme(
                            row.verificationStatus,
                          )}
                        >
                          {row.verificationStatus || "pending"}
                        </Badge>
                      </Td>

                      <Td>{row.isDefault ? "Yes" : "No"}</Td>

                      <Td>{formatDateTime(row.createdAt)}</Td>

                      <Td>
                        <HStack>
                          {row.verificationStatus !== "verified" && (
                            <Button
                              size="xs"
                              onClick={() => void verify(row, "verified")}
                              isLoading={rowBusy}
                              isDisabled={!canManage || !rowId}
                            >
                              Verify
                            </Button>
                          )}

                          {row.verificationStatus !== "failed" && (
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={() => void verify(row, "failed")}
                              isLoading={rowBusy}
                              isDisabled={!canManage || !rowId}
                            >
                              Fail
                            </Button>
                          )}

                          {!row.isDefault && (
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={() => void setDefault(row)}
                              isLoading={rowBusy}
                              isDisabled={!canManage || !rowId}
                            >
                              Default
                            </Button>
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>

            {!rows.length && (
              <Text mt={4} color={theme.textMuted}>
                {isSuperAdmin
                  ? "No payout destinations found."
                  : "No payout destinations are linked to your account."}
              </Text>
            )}
          </Box>
        )}
      </FinanceSectionCard>
    </FinancePageShell>
  );
};

export default AdminPayoutDestinationsPage;
